/**
 * Shared ROI report UI helpers (formatting, staleness).
 */

import type { PleriRoiReport } from '../../../../../api/pleri-roi-reports';
import { formatLocale } from '@plerionhq/design-system';

export function formatMonthLabel(month: string | undefined | null): string {
  if (month == null || typeof month !== 'string') {
    return '';
  }
  const parts = month.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  if (y == null || m == null || Number.isNaN(y) || Number.isNaN(m)) {
    return month;
  }
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString(formatLocale(), {
    month: 'long',
    year: 'numeric',
  });
}

export function formatGeneratedAt(iso: string | undefined | null): string {
  if (iso == null || typeof iso !== 'string') {
    return '';
  }
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

/** ~2 hours in ms; backend treats in-progress older than this as stale. */
const STALE_GENERATION_MS = 2 * 60 * 60 * 1000;

/** ~1 hour in ms; show "taking longer than usual" hint after this. */
export const LONG_RUNNING_GENERATION_MS = 60 * 60 * 1000;

export function isGenerationStale(report: PleriRoiReport): boolean {
  const started = report.generationStartedAt;
  if (started == null || typeof started !== 'string') {
    return false;
  }
  try {
    const startedAt = new Date(started).getTime();
    return Date.now() - startedAt > STALE_GENERATION_MS;
  } catch {
    return false;
  }
}

export function isGenerationLongRunning(report: PleriRoiReport): boolean {
  const started = report.generationStartedAt;
  if (started == null || typeof started !== 'string') {
    return false;
  }
  try {
    const startedAt = new Date(started).getTime();
    return Date.now() - startedAt > LONG_RUNNING_GENERATION_MS;
  } catch {
    return false;
  }
}

export function formatStartedAt(iso: string | undefined | null): string {
  if (iso == null || typeof iso !== 'string') {
    return '';
  }
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffMins < 1) {
      return 'Started just now';
    }
    if (diffMins < 60) {
      return `Started ${diffMins} min ago`;
    }
    if (diffHours < 24) {
      return `Started ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    }
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}
